
































































































import { EventUIDto } from "@/lib/dto/event/event-ui.dto";
import { RefTypeMap } from "@/lib/utility/ref-type.map";
import {
  MrfiktivReferenceGen,
  MrfiktivReportViewModelGen,
  MrfiktivEventViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { IRefSuggestion, RefSelectType } from "@/store/modules/fleet-aggregation.store";
import debounce from "debounce";
import { Component, Prop, Vue } from "vue-property-decorator";
import VirtualList from "vue-virtual-scroll-list";
import draggable from "vuedraggable";
import LatestEntriesCardEmpty from "../cards/LatestEntriesCardEmpty.vue";
import RefsList from "../utility/RefsList.vue";
import AEicon from "./AEicon.vue";
import RefsSelectionSuggestion from "./RefsSelectionSuggestion.vue";
import Tooltip from "./tooltip.vue";

@Component({
  components: { RefsList, draggable, VirtualList, Tooltip, RefsSelectionSuggestion, AEicon, LatestEntriesCardEmpty },
  filters: {}
})
export default class RefsSelection extends Vue {
  @Prop()
  private value!: MrfiktivReferenceGen[];

  @Prop({ default: () => [] })
  suggested!: IRefSuggestion[];

  @Prop()
  partnerId!: string;

  @Prop({
    default: () => [
      BackendResourceEnum.VEHICLE,
      BackendResourceEnum.EVENT,
      BackendResourceEnum.REPORT,
      BackendResourceEnum.TICKET
    ]
  })
  categories!: BackendResourceEnum[];

  @Prop({ default: false })
  loading!: boolean;

  /**
   * Are new references added to the top or the bottom of the reference list
   */
  @Prop()
  addToTop?: boolean;

  readonly SUGGESTION = "suggestion";

  readonly RefTypeMap = RefTypeMap;

  readonly BackendResourceEnum = BackendResourceEnum;

  searchFilter = "";

  debouncedSearchFilter = "";

  debounceSetSearchFilter = debounce(this.setSearchFilter, 300);

  isLoadingMore = false;

  selectedCategory =
    this.suggested.length + this.value.filter(d => d.refType === BackendResourceEnum.VEHICLE).length
      ? this.SUGGESTION
      : this.categories[0];

  get selected(): MrfiktivReferenceGen[] {
    return this.value;
  }
  set selected(v: MrfiktivReferenceGen[]) {
    this.$emit("input", v);
  }

  get list() {
    const list = RefTypeMap.get(this.selectedCategory as BackendResourceEnum)?.module.paginationList;

    if (!list) {
      return [];
    }

    // some events do not have an ID which is a problem for the virtual-list that needs a unique key. generate an id for events without an ID here
    if (this.selectedCategory === BackendResourceEnum.EVENT) {
      list.forEach((item: MrfiktivEventViewModelGen) => {
        if (!item.id) {
          item.id = (item.recurringEventId ?? "") + item.start;
        }
      });
    }

    return list.filter((item: any) => !this.isSelected(item) && this.matchesFilter(item));
  }

  getRefTypeName(refType: BackendResourceEnum) {
    if (refType === BackendResourceEnum.EVENT) {
      return this.$t("common.nouns.appointments");
    }
    return this.$t(`BackendResourceEnum.${refType}`);
  }

  setSearchFilter() {
    this.debouncedSearchFilter = this.searchFilter;
  }

  matchesFilter(filtereee: Record<string, any>): boolean {
    if (!this.debouncedSearchFilter) {
      return true;
    }

    return JSON.stringify(filtereee)
      .toLowerCase()
      .includes(this.debouncedSearchFilter.toLowerCase());
  }

  isSelected(ref: RefSelectType) {
    return !!this.selected.find(selected => {
      if (!selected.refId) {
        return false;
      }

      return ref.id === selected.refId;
    });
  }

  isSuggested(ref: RefSelectType) {
    return !!this.suggested.find(suggested => {
      if (!suggested.ref.id) {
        return false;
      }
      return ref.id === suggested.ref.id;
    });
  }

  async addToSelected(item: IRefSuggestion) {
    const selection: MrfiktivReferenceGen[] = [];

    if (item.refType === BackendResourceEnum.EVENT) {
      const event = item.ref as EventUIDto;

      if ((item.ref as EventUIDto).isVirtual || !item.ref.id) {
        // virtual events must be created first before selection
        await event.createVirtual();
        if (event.isVirtual) {
          // instanciation did not work
          return;
        }
        item.ref = event;
      }

      // also add the vehicle if you add an event
      if (event.vehicleId && !this.selected.find(selected => selected.refId === event.vehicleId)) {
        selection.push({
          refId: event.vehicleId,
          refType: BackendResourceEnum.VEHICLE
        });
      }
    }

    if (item.refType === BackendResourceEnum.REPORT) {
      const report = item.ref as MrfiktivReportViewModelGen;

      if (report.vehicleId && !this.selected.find(selected => selected.refId === report.vehicleId)) {
        selection.push({
          refId: report.vehicleId,
          refType: BackendResourceEnum.VEHICLE
        });
      }
    }

    if (!item.ref.id) {
      return;
    }

    selection.push({ refId: item.ref.id, refType: item.refType });

    if (this.addToTop) {
      this.selected.unshift(...selection);
    } else {
      this.selected.push(...selection);
    }

    this.$forceUpdate();
    this.$emit("added");
  }

  async loadMore(refType: BackendResourceEnum) {
    const module = RefTypeMap.get(refType)?.module;

    if (!module) {
      return;
    }

    try {
      this.isLoadingMore = true;
      module.setFilter([]);
      await module.fetchNext({ partnerId: this.partnerId });
    } catch (e) {
      this.$log.error(e);
    } finally {
      this.isLoadingMore = false;
    }
  }
}
